import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {UnreachableError} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeader} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {DropdownList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {UserTheme} from '../../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {SmartDateTimeFormatterContainer} from '../../../../../srcCommon/formatters/dateTime/smartDateTimeFormatterContainer';
import {InboxModel} from '../../../../models/api/inboxModel';
import {RefinementOptionModel} from '../../../../models/api/refinementOptionModel';
import {TagModel} from '../../../../models/api/tagModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {LensCriterion, LensCriterionTypes} from '../../../../store/data/refinements/refinementsSelectors';
import {RefinedInboxParameters} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {DropdownCategory} from '../../../common/dropdowns/dropdownCategory';
import {Pill} from '../../../pill/pill';
import {
  REFINEMENT_SEARCH_DROPDOWN_HEIGHT,
  REFINEMENT_SEARCH_DROPDOWN_TITLE,
  REFINEMENT_SEARCH_DROPDOWN_WIDTH,
  renderRefinementSearchDropdownHeaderRight,
} from './cellsRefinementOptionsBaseSearchDropdown';
import {CellsRefinementOptionsSearchAssigneesDropdownRow} from './cellsRefinementOptionsSearchAssigneesDropdownRow';
import {CellsRefinementOptionsSearchDropdownNavigationItem} from './cellsRefinementOptionsSearchDropdownNavigationItem';
import {CellsRefinementOptionsSearchInboxesDropdownRow} from './cellsRefinementOptionsSearchInboxesDropdownRow';
import {
  CellsRefinementOptionsSearchTagsDropdownRow,
  SearchTagsDropdownItemTypes,
} from './cellsRefinementOptionsSearchTagsDropdownRow';

/*
 * Props.
 */

interface CellsRefinementOptionsSearchDropdownNavigationProps {
  parameters: RefinedInboxParameters;
  popoverProps: PopoverTransferProps;
  selectedRefinement: RefinementOptionModel | undefined;
  lensCriteria: ReadonlyArray<LensCriterion>;
  hasAssigneeLensCriteria: boolean;
  lensUpdatedAt: number | undefined;

  selectedAssignee: TeammateModel | undefined;
  onSelectAssignee: HandlerOf<TeammateModel>;
  onNavigateToSearchAssignees: Handler;

  onSelectInbox: HandlerOf<InboxModel>;

  selectedTags: ReadonlyArray<TagModel> | undefined;
  onSelectTag: HandlerOf<TagModel>;
  onNavigateToSearchTags: Handler;
  tagNamespaces: ReadonlyArray<string>;
}

/*
 * Styles.
 */

const StyledUpdatedAtPill = styled(Pill)`
  margin-left: 4px;
`;

/*
 * Component.
 */

export const CellsRefinementOptionsSearchDropdownNavigation: FC<
  CellsRefinementOptionsSearchDropdownNavigationProps
> = (props) => {
  const {
    popoverProps,
    parameters,
    selectedAssignee,
    selectedTags,
    lensCriteria,
    onNavigateToSearchAssignees,
    onNavigateToSearchTags,
    selectedRefinement,
    onSelectTag,
    onSelectAssignee,
    hasAssigneeLensCriteria,
    lensUpdatedAt,
  } = props;
  const theme = useUserTheme();

  // De-duplicates the dropdown menu selection if a tag criterion matches the tag refinement.
  const lensCriteriaTagIds = computeLensCriteriaTagIds(lensCriteria);
  const refiningTags =
    selectedTags !== undefined &&
    selectedTags.length !== 0 &&
    !lensCriteriaTagIds.includes(selectedTags[0].id);

  // De-duplicates the dropdown menu selection if an assignee criterion matches the assignee refinement.
  const lensCriteriaAssigneeIds = computeLensCriteriaAssigneeIds(lensCriteria);
  const refiningAssignee =
    selectedAssignee !== undefined && !lensCriteriaAssigneeIds.includes(selectedAssignee.id);

  return (
    <Dropdown
      anchor={popoverProps.anchor}
      onRequestClose={popoverProps.onRequestClose}
      width={REFINEMENT_SEARCH_DROPDOWN_WIDTH}
      height={REFINEMENT_SEARCH_DROPDOWN_HEIGHT}
    >
      <DropdownHeader
        title={REFINEMENT_SEARCH_DROPDOWN_TITLE}
        rightIcon={renderRefinementSearchDropdownHeaderRight(popoverProps, selectedRefinement, parameters)}
      />
      <DropdownList>
        {selectedTags !== undefined && refiningTags && (
          <CellsRefinementOptionsSearchTagsDropdownRow
            selectedTags={selectedTags}
            onSelectTag={onSelectTag}
            item={{type: SearchTagsDropdownItemTypes.UNSELECTED as const, tags: selectedTags}}
            shouldNest={false}
            isNotInteractive
          />
        )}

        {selectedAssignee !== undefined && refiningAssignee && (
          <CellsRefinementOptionsSearchAssigneesDropdownRow
            selectedRefinement={selectedRefinement}
            onSelectAssignee={onSelectAssignee}
            assignee={selectedAssignee}
            isNotInteractive
          />
        )}

        {lensCriteria.length !== 0 && (
          <>
            <DropdownCategory>
              <Trans id="cells-refinement-options-search-dropdown-navigation-category-criteria">
                Criteria
              </Trans>
              {maybeRenderLensUpdatedAt(theme, lensUpdatedAt)}
            </DropdownCategory>
            {renderLensCriteria(props, lensCriteria)}
          </>
        )}

        {(refiningTags || refiningAssignee || lensCriteria.length !== 0) && (
          <DropdownCategory>
            <Trans id="cells-refinement-options-search-dropdown-navigation-category-more">More</Trans>
          </DropdownCategory>
        )}

        <CellsRefinementOptionsSearchDropdownNavigationItem
          key="search-tags"
          iconName="tag"
          label={<Trans id="cells-refinement-options-search-dropdown-navigation-item-tags">Tags</Trans>}
          onSelect={onNavigateToSearchTags}
        />
        {!hasAssigneeLensCriteria && (
          <CellsRefinementOptionsSearchDropdownNavigationItem
            key="search-assignees"
            iconName="assign"
            label={
              <Trans id="cells-refinement-options-search-dropdown-navigation-item-assignees">Assignees</Trans>
            }
            onSelect={onNavigateToSearchAssignees}
          />
        )}
      </DropdownList>
    </Dropdown>
  );
};

function renderLensCriteria(
  props: CellsRefinementOptionsSearchDropdownNavigationProps,
  lensCriteria: ReadonlyArray<LensCriterion>,
) {
  const {popoverProps, selectedRefinement, selectedTags, onSelectAssignee, onSelectInbox, onSelectTag} =
    props;

  return (
    <>
      {lensCriteria.map((criterion) => {
        switch (criterion.type) {
          case LensCriterionTypes.ASSIGNEE:
            return (
              <CellsRefinementOptionsSearchAssigneesDropdownRow
                key={criterion.assignee.id}
                selectedRefinement={selectedRefinement}
                onSelectAssignee={makeOnSelect<TeammateModel>(popoverProps, onSelectAssignee)}
                assignee={criterion.assignee}
                isNotInteractive={false}
              />
            );
          case LensCriterionTypes.INBOX:
            return (
              <CellsRefinementOptionsSearchInboxesDropdownRow
                key={criterion.inbox.id}
                selectedRefinement={selectedRefinement}
                onSelectInbox={makeOnSelect<InboxModel>(popoverProps, onSelectInbox)}
                inbox={criterion.inbox}
                isNotInteractive={false}
              />
            );
          case LensCriterionTypes.TAGS:
            return (
              <CellsRefinementOptionsSearchTagsDropdownRow
                key={criterion.tags[0]?.id}
                selectedTags={selectedTags}
                onSelectTag={makeOnSelect<TagModel>(popoverProps, onSelectTag)}
                item={{type: SearchTagsDropdownItemTypes.UNSELECTED as const, tags: criterion.tags}}
                shouldNest={false}
                isNotInteractive={false}
              />
            );
          default:
            throw new UnreachableError(criterion);
        }
      })}
    </>
  );
}

function maybeRenderLensUpdatedAt(theme: UserTheme, lensUpdatedAt: number | undefined) {
  if (!lensUpdatedAt) {
    return null;
  }

  return (
    <StyledUpdatedAtPill
      size={VisualSizesEnum.MEDIUM}
      backgroundColor={theme.greys.shade30}
      color={theme.greys.shade80}
    >
      Updated{' '}
      <SmartDateTimeFormatterContainer
        customFormat={{year: 'numeric', month: 'short', day: 'numeric'}}
        value={lensUpdatedAt}
      />
    </StyledUpdatedAtPill>
  );
}

function computeLensCriteriaAssigneeIds(lensCriteria: ReadonlyArray<LensCriterion>) {
  return lensCriteria.reduce<Array<number>>((acc, criterion) => {
    if (criterion.type === LensCriterionTypes.ASSIGNEE) {
      acc.push(criterion.assignee.id);
    }
    return acc;
  }, []);
}

function computeLensCriteriaTagIds(lensCriteria: ReadonlyArray<LensCriterion>) {
  return lensCriteria.reduce<Array<number>>((acc, criterion) => {
    if (criterion.type === LensCriterionTypes.TAGS && criterion.tags.length !== 0) {
      acc.push(criterion.tags[0].id);
    }
    return acc;
  }, []);
}

function makeOnSelect<T>(popoverProps: PopoverTransferProps, handler: HandlerOf<T>) {
  return (item: T) => {
    popoverProps.onRequestClose();
    return handler(item);
  };
}
